/**
 * VendorPassportSection (P362) -- the vendor drawer's resilience
 * passport panel (killer-features F4).
 *
 * Shows the latest ingested passport (attested name, key, expiry,
 * attestations) and takes an upload. The trust flow is surfaced
 * honestly: a 409 (no recorded key) renders the key_id + a visible
 * "trust on first use" confirmation; a 422 (pin mismatch / invalid)
 * renders the server's reason verbatim.
 */
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { currentAuthToken } from "shared/api/client";
import { useMe } from "shared/api/useMe";

interface VendorPassportOut {
  id: string;
  vendor_id: string;
  passport_id: string;
  vendor_name_attested: string;
  issued_at: string;
  expires_at: string;
  is_current: boolean;
  attestations: { key?: string; statement?: string; attested_at?: string }[];
  continuity: Record<string, unknown>;
  key_id: string;
  key_source: string | null;
  ingested_at: string;
}

async function fetchLatest(vendorId: string): Promise<VendorPassportOut | null> {
  const token = currentAuthToken();
  const r = await fetch(`/v1/admin/foundation/vendors/${vendorId}/passport`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (r.status === 404) return null;
  // Round 20 Q8 -- passports are an administrator's; say so rather
  // than reading a refusal as "no passport on record".
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const body = (await r.json()) as VendorPassportOut;
  // Defensive: an unexpected 200 shape (test stubs, proxies) must not
  // crash the drawer -- treat anything without a passport_id as absent.
  return body && typeof body.passport_id === "string" ? body : null;
}

async function upload(
  vendorId: string,
  file: File,
  trustFirstUse: boolean,
): Promise<{ ok: true } | { ok: false; status: number; detail: string }> {
  const token = currentAuthToken();
  const form = new FormData();
  form.append("file", file);
  const r = await fetch(
    `/v1/admin/foundation/vendors/${vendorId}/passport` +
      (trustFirstUse ? "?trust_first_use=true" : ""),
    {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    },
  );
  if (r.ok) return { ok: true };
  let detail = `HTTP ${r.status}`;
  try {
    detail = ((await r.json()) as { detail?: string }).detail ?? detail;
  } catch {
    // keep the status text
  }
  return { ok: false, status: r.status, detail };
}

export function VendorPassportSection({ rowId }: { rowId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // Round 20 Q8 -- the seat without the role was offered the upload on
  // a register it may read but not change; the server refuses both the
  // read and the upload to anyone but an administrator.
  const me = useMe();
  const isAdmin = me.data?.is_admin === true;
  const fileRef = useRef<HTMLInputElement>(null);
  const [trustFirstUse, setTrustFirstUse] = useState(false);
  const [serverMessage, setServerMessage] = useState<string | null>(null);

  const latest = useQuery({
    queryKey: ["vendor-passport", rowId],
    queryFn: () => fetchLatest(rowId),
    retry: false,
  });

  const ingest = useMutation({
    mutationFn: async () => {
      const file = fileRef.current?.files?.[0];
      if (!file) throw new Error("no file");
      return upload(rowId, file, trustFirstUse);
    },
    onSuccess: (result) => {
      if (result.ok) {
        setServerMessage(null);
        setTrustFirstUse(false);
        if (fileRef.current) fileRef.current.value = "";
        void qc.invalidateQueries({ queryKey: ["vendor-passport", rowId] });
      } else {
        setServerMessage(result.detail);
      }
    },
    onError: () => setServerMessage(t("vendorPassport.uploadFailed")),
  });

  const p = latest.data;
  return (
    <section data-testid="vendor-passport-section" className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {t("vendorPassport.heading")}
      </h3>

      {latest.isError && (latest.error as Error).message === "forbidden" ? (
        <p data-testid="vendor-passport-refused" className="text-sm text-slate-500">
          {t("vendorPassport.adminOnly")}
        </p>
      ) : latest.isLoading ? null : p ? (
        <div
          data-testid="vendor-passport-current"
          className="space-y-1 rounded border border-paper-2 bg-white p-3 text-sm"
        >
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-medium text-slate-900">{p.vendor_name_attested}</span>
            {p.is_current ? (
              <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-800">
                {t("vendorPassport.currentUntil", {
                  date: p.expires_at.slice(0, 10),
                })}
              </span>
            ) : (
              <span className="rounded bg-rose-100 px-1.5 py-0.5 text-xs font-semibold text-rose-800">
                {t("vendorPassport.expired")}
              </span>
            )}
            <span className="font-mono text-xs text-slate-500">
              {t("screens.vendorPassportSection.key")} {p.key_id}
              {p.key_source === "first_use" && ` · ${t("vendorPassport.firstUse")}`}
            </span>
          </div>
          <ul className="space-y-0.5">
            {(Array.isArray(p.attestations) ? p.attestations : []).map((a, i) => (
              <li key={i} className="text-xs text-slate-600">
                <span className="font-mono text-slate-500">{a.key}</span>
                {a.statement ? ` — ${a.statement}` : null}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p data-testid="vendor-passport-none" className="text-sm text-slate-500">
          {t("vendorPassport.none")}
        </p>
      )}

      {isAdmin && (
        <div className="space-y-2 rounded border border-paper-2 bg-white p-3">
          <input
            ref={fileRef}
            type="file"
            accept=".zip,application/zip"
            data-testid="vendor-passport-file"
            className="block text-xs"
          />
          <label className="flex items-center gap-1 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={trustFirstUse}
              onChange={(e) => setTrustFirstUse(e.target.checked)}
              data-testid="vendor-passport-tofu"
            />
            {t("vendorPassport.tofu")}
          </label>
          <button
            type="button"
            onClick={() => ingest.mutate()}
            disabled={ingest.isPending}
            data-testid="vendor-passport-upload"
            className="rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
          >
            {ingest.isPending
              ? t("vendorPassport.uploading")
              : t("vendorPassport.upload")}
          </button>
          {serverMessage && (
            <p data-testid="vendor-passport-message" className="text-xs text-amber-800">
              {serverMessage}
            </p>
          )}
          <p className="text-xs text-slate-400">{t("vendorPassport.hint")}</p>
        </div>
      )}
    </section>
  );
}
